import { BodyImage } from './BodyImage';
import { ARTICLE_TOKENS } from '../design-tokens';
import type { PlannerGameweek } from '@api-service/planner';
import TeamLineup from '@components/team-lineup';
import { useRevealPlannedTeam } from '@hooks/useRevealPlannedTeam';
import {
  buildPlannedTeamLineups,
  describePlannedTeam
} from '@lib/article-blocks/planned-team';
import type { SanityPlannedTeamBlock } from '@lib/article-blocks/types';

// Roughly a pitch and bench, so the article does not jump when the team lands.
const LOADING_MIN_HEIGHT = '500px';

/**
 * The legacy article stylesheet (`.article h3` and `.article img` in
 * scss/_article.scss) adds bottom margins that push the heading off its row
 * and the armband images out of the captain badges; the important modifiers
 * undo them inside the box.
 */
export const PlannedTeamPitch = ({
  gameweek
}: {
  gameweek: PlannerGameweek;
}) => {
  const { starting, bench } = buildPlannedTeamLineups(gameweek);

  return (
    <section className="not-prose my-8 [&_img]:!mb-0" data-cy="planned-team">
      <header
        className={`mb-4 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b pb-2 ${ARTICLE_TOKENS.rule}`}
      >
        <h3
          className={`!m-0 font-heading text-xl font-bold leading-tight ${ARTICLE_TOKENS.ink}`}
        >
          Gameweek {gameweek.gameweek} team reveal
        </h3>
        <span
          className={`font-body text-sm tabular-nums ${ARTICLE_TOKENS.muted}`}
          data-cy="planned-team-summary"
        >
          {describePlannedTeam(gameweek)}
        </span>
      </header>
      <TeamLineup starting={starting} bench={bench} />
    </section>
  );
};

const Screenshot = ({ value }: { value: SanityPlannedTeamBlock }) =>
  value.screenshot?.asset ? (
    <BodyImage value={{ ...value.screenshot, _type: 'image' }} />
  ) : null;

/**
 * Only members ever render this: the box is allowed in the premium body
 * alone. So a placeholder while loading shows no one a team they may not see,
 * and if the team cannot be loaded the screenshot stands in for it.
 */
const PlannerTeamBox = ({ value }: { value: SanityPlannedTeamBlock }) => {
  const state = useRevealPlannedTeam(value._key);

  if (state.status === 'ready') {
    return <PlannedTeamPitch gameweek={state.gameweek} />;
  }
  if (state.status === 'loading') {
    return (
      <div
        className="not-prose my-8 rounded-lg bg-neutral-100 animate-pulse"
        style={{ minHeight: LOADING_MIN_HEIGHT }}
        data-cy="planned-team-loading"
      />
    );
  }
  return <Screenshot value={value} />;
};

/**
 * The contributor's team: their Hub planner team when the box has an FPL ID,
 * otherwise the screenshot every box carries (which the app shows too).
 */
export const PlannedTeamBlock = ({
  value
}: {
  value: SanityPlannedTeamBlock;
}) =>
  value.fplTeamId ? (
    <PlannerTeamBox value={value} />
  ) : (
    <Screenshot value={value} />
  );
